import { Key, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import filterIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/filter.svg'
import exportIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/export.svg'
import arrowLeft from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/arrow-left.png'
import arrowRight from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/arrow-right.svg'

import { DateTimeFormatter } from 'Helpers/DateTimeFormatter'
import { HorizontalLoader } from 'Components'

import './OrderBoost.scss'
import {
  OrderProduct,
  OrderStatus,
  OrderXModel,
  PaymentStatus,
} from 'BoostXpressAdmin/Model/orderXmodel'
import { orderBXService } from 'BoostXpressAdmin/Services/orderBXService'
import { CurrencyFormatter } from 'Helpers'
import fillingStationsService, {
  FillingStation,
} from 'BoostXpressAdmin/Services/fillingStationsService'

const OrderBoost = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [pageNumber, setPageNumber] = useState(1)
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [pageSize, setPageSize] = useState(50)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [stationId, setStationId] = useState<string | null | undefined>(undefined)

  const [stations, setStations] = useState<FillingStation[]>([])

  // Filter states
  const [showFilters, setShowFilters] = useState(false)
  // const [stationId, setStationId] = useState('')
  // const [businessName, setBusinessName] = useState('')
  const [total, setTotal] = useState(0)
  const [orderCount, setOrderCount] = useState(0)
  const [isOrderCountLoading, setIsOrderCountLoading] = useState(false)

  const [status, setStatus] = useState<OrderStatus | ''>('')
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | ''>('')
  const [product, setProduct] = useState<OrderProduct | ''>('')

  const [userResourceId, setUserResourceId] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const [searchEndpoint, setSearchEndpoint] = useState('')

  useEffect(() => {
    const userResId = searchParams.get('userResourceId')
    if (userResId) {
      setSearch(userResId)
      setUserResourceId(userResId)
    }
  }, [searchParams])

  useEffect(() => {
    const t = window.setTimeout(() => {
      const trimmedSearch = search.trim()
      const trimmedUserResId = userResourceId.trim()

      let endpoint = ''
      if (trimmedSearch) {
        endpoint += `userResourceId=${encodeURIComponent(trimmedSearch)}`
      } else if (trimmedUserResId) {
        endpoint += `userResourceId=${encodeURIComponent(trimmedUserResId)}`
      }

      setSearchEndpoint(endpoint)
    }, 350)
    return () => window.clearTimeout(t)
  }, [search, userResourceId])

  const handleClearFilters = () => {
    setStationId(undefined)
    setStatus('')
    setPaymentStatus('')
    setProduct('')
    setUserResourceId('')
    setStartDate('')
    setEndDate('')
    setSearch('')
    setSearchEndpoint('')
    setPageNumber(1)
    setShowFilters(false)
  }

  const [orders, setOrders] = useState<OrderXModel[]>([])

  const fetchOrders = async () => {
    setIsLoading(true)

    const params: {
      page: number
      limit: number
      sortOrder: 'DESC'
      status?: OrderStatus
      paymentStatus?: PaymentStatus
      product?: OrderProduct
      userResourceId?: string
      stationId?: string
      startDateYYYY_MM_DD?: string
      endDateYYYY_MM_DD?: string
    } = {
      page: pageNumber,
      limit: pageSize,
      sortOrder: 'DESC',
      status: status || undefined,
      paymentStatus: paymentStatus || undefined,
      product: product || undefined,
      stationId: stationId || undefined,
    }

    if (searchEndpoint) {
      const sParams = searchEndpoint.split('&')
      sParams.forEach((param) => {
        const [key, value] = param.split('=')
        if (key === 'userResourceId') params.userResourceId = value
      })
    }

    if (startDate) params['startDateYYYY_MM_DD'] = startDate
    if (endDate) params['endDateYYYY_MM_DD'] = endDate

    await orderBXService.list(params).then(
      (res) => {
        const responseData = res.data?.data
        const nextOrders = Array.isArray(responseData)
          ? responseData
          : Array.isArray(responseData?.dataset)
            ? responseData.dataset
            : []
        const pagination = res.data?.meta?.pagination ?? responseData?.meta?.pagination

        setOrders(nextOrders)
        setTotal(pagination?.total ?? responseData?.total ?? nextOrders.length)
        setPageSize(pagination?.per_page ?? pageSize)
      },
      () => {
        setOrders([])
        setTotal(0)
      },
    )

    setIsLoading(false)
  }

  useEffect(() => {
    fetchOrders()
  }, [
    pageNumber,
    pageSize,
    status,
    paymentStatus,
    product,
    startDate,
    endDate,
    stationId,
    searchEndpoint,
  ])

  useEffect(() => {
    let cancelled = false

    const loadSummary = async () => {
      setIsOrderCountLoading(true)
      try {
        const params: {
          status?: OrderStatus
          paymentStatus?: PaymentStatus
          product?: OrderProduct
          userResourceId?: string
          stationId?: string
          startDateYYYY_MM_DD?: string
          endDateYYYY_MM_DD?: string
        } = {
          status: status || undefined,
          paymentStatus: paymentStatus || undefined,
          product: product || undefined,
          stationId: stationId || undefined,
        }

        if (searchEndpoint) {
          const sParams = searchEndpoint.split('&')
          sParams.forEach((param) => {
            const [key, value] = param.split('=')
            if (key === 'userResourceId') params.userResourceId = value
          })
        }

        if (startDate) params['startDateYYYY_MM_DD'] = startDate
        if (endDate) params['endDateYYYY_MM_DD'] = endDate

        const res = await orderBXService.ordersSummary(params)
        if (cancelled) return

        const summaryData = res.data?.data ?? {}
        const totalCostMajor = summaryData?.totalCostMajor

        setOrderCount(Number(totalCostMajor ?? 0))
      } catch (error) {
        if (!cancelled) {
          console.error('Order summary fetch failed:', error)
          setOrderCount(0)
        }
      } finally {
        if (!cancelled) setIsOrderCountLoading(false)
      }
    }

    loadSummary()

    return () => {
      cancelled = true
    }
  }, [status, paymentStatus, product, stationId, startDate, endDate, searchEndpoint])

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (target?.closest('[data-station-menu-root="true"]')) return
      setOpenMenuId(null)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [])

  const totalPages = useMemo(() => {
    if (!total) return 1
    return Math.max(1, Math.ceil(total / pageSize))
  }, [pageSize, total])

  const getStations = async () => {
    const params: any = {
      pageNumber,
      sortOrder: 'DESC',
    }

    await fillingStationsService.list(params).then((res) => {
      setStations(res.data?.dataset ?? [])
    })
  }

  useEffect(() => {
    getStations()
  }, [pageNumber])

  const handleExport = async () => {
    try {
      setIsExporting(true)

      const params: any = {
        sortOrder: 'DESC',
        status: status || undefined,
        paymentStatus: paymentStatus || undefined,
        product: product || undefined,
        stationId: stationId || undefined,
      }

      if (searchEndpoint) {
        const sParams = searchEndpoint.split('&')
        sParams.forEach((param) => {
          const [key, value] = param.split('=')
          if (key === 'userResourceId') params.userResourceId = value
        })
      }

      if (startDate) params['startDateYYYY_MM_DD'] = startDate
      if (endDate) params['endDateYYYY_MM_DD'] = endDate

      const response = await orderBXService.csvExport(params)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `orders-export-${new Date().getTime()}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className='bx-stations-order-list'>
      <div className='bx-stations-list__header'>
        <h2 className='bx-stations-list__title'>Orders</h2>

        <div className='order-count'>
          <div className='card border-0 shadow-sm'>
            <div className='card-body'>
              <span className='fw-bolder'>Total Orders: </span>
              {isOrderCountLoading ? (
                <span
                  className='spinner-border spinner-border-sm'
                  role='status'
                  aria-hidden='true'
                ></span>
              ) : (
                <span className='fw-bolder text-default-1'>₦{CurrencyFormatter(orderCount)}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='bx-stations-list__content'>
        <div className='bx-stations-list__toolbar row g-2 align-items-center'>
          <div className='col-12 col-md-8 col-lg-8 col-xxl-10'>
            <div className='bx-stations-search w-100'>
              <div className='bx-stations-search__icon'>
                <svg
                  width='17'
                  height='17'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z'
                    stroke='#868484'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <input
                type='text'
                className='bx-stations-search__input'
                placeholder='User Resource ID'
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setPageNumber(1)
                }}
              />
            </div>
          </div>

          <div className='col-6 col-md-2 col-lg-2 col-xxl-1 position-relative'>
            <button
              className='bx-stations-actions__btn w-100'
              onClick={() => setShowFilters(!showFilters)}
            >
              <img src={filterIcon} alt='' className='bx-stations-actions__icon' />
              <span className='bx-stations-actions__label'>Filters</span>
            </button>
            {showFilters && (
              <div className='bx-stations-filters-dropdown'>
                <div className='bx-filters-header'>
                  <h5>Filter Orders</h5>

                  <button className='bx-filters-close' onClick={() => setShowFilters(false)}>
                    ×
                  </button>
                </div>

                <div className='bx-filters-body'>
                  <div className='bx-filter-group'>
                    <label>Station</label>

                    <select
                      className='form-control'
                      value={stationId || ''}
                      onChange={(e) => setStationId(e.target.value)}
                    >
                      <option value=''>All Stations</option>
                      {stations.map((s) => (
                        <option value={s.id} key={s.id}>
                          {s.businessName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className='bx-filter-group'>
                    <label>Status</label>

                    <select
                      className='form-control'
                      value={status}
                      onChange={(e) => setStatus(e.target.value as OrderStatus)}
                    >
                      <option value=''>All Status</option>
                      <option value='CREATED'>CREATED</option>
                      <option value='IN_PROGRESS'>IN_PROGRESS</option>
                      <option value='COMPLETED'>COMPLETED</option>
                      <option value='CANCELLED'>CANCELLED</option>
                    </select>
                  </div>

                  <div className='bx-filter-group'>
                    <label>Payment Status</label>

                    <select
                      className='form-control'
                      value={paymentStatus}
                      onChange={(e) => setPaymentStatus(e.target.value as PaymentStatus)}
                    >
                      <option value=''>All</option>
                      <option value='paid'>paid</option>
                      <option value='unpaid'>unpaid</option>
                      <option value='failed'>failed</option>
                      <option value='pending'>pending</option>
                      <option value='refunded'>refunded</option>
                    </select>
                  </div>

                  <div className='bx-filter-group'>
                    <label>Product</label>

                    <select
                      className='form-control'
                      value={product}
                      onChange={(e) => setProduct(e.target.value as OrderProduct)}
                    >
                      <option value=''>All</option>
                      <option value='PMS'>PMS</option>
                      <option value='AGO'>AGO</option>
                      <option value='LPG'>LPG</option>
                    </select>
                  </div>

                  <div className='bx-filter-group'>
                    <label>User Resource ID</label>

                    <input
                      type='text'
                      className='form-control'
                      value={userResourceId}
                      onChange={(e) => setUserResourceId(e.target.value)}
                    />
                  </div>

                  <div className='bx-filter-group'>
                    <label>Start Date</label>

                    <input
                      type='date'
                      className='form-control'
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>

                  <div className='bx-filter-group'>
                    <label>End Date</label>

                    <input
                      type='date'
                      className='form-control'
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <button
                    type='button'
                    className='btn btn-default px-3'
                    onClick={handleClearFilters}
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className='col-6 col-md-2 col-lg-2 col-xxl-1'>
            <button
              className='bx-stations-actions__btn w-100'
              onClick={handleExport}
              disabled={isExporting || isLoading}
            >
              <img src={exportIcon} alt='' className='bx-stations-actions__icon' />
              <span className='bx-stations-actions__label'>
                {isExporting ? (
                  <span
                    className='spinner-border spinner-border-sm'
                    role='status'
                    aria-hidden='true'
                  ></span>
                ) : (
                  'Export'
                )}
              </span>
            </button>
          </div>
        </div>

        <div className='bx-stations-table'>
          <div className='bx-table-wrapper'>
            <table className='bx-stations-table__table'>
              <thead>
                <tr>
                  <th className='bx-stations-table__th bx-stations-table__th--name'>S/N</th>
                  <th className='bx-stations-table__th bx-stations-table__th--name'>Reference</th>
                  <th className='bx-stations-table__th bx-stations-table__th--vol'>Customer</th>
                  <th className='bx-stations-table__th bx-stations-table__th--city'>Product</th>
                  <th className='bx-stations-table__th bx-stations-table__th--vol'>Quantity</th>
                  <th className='bx-stations-table__th bx-stations-table__th--seen'>Amount</th>
                  <th className='bx-stations-table__th bx-stations-table__th--status'>Payment</th>
                  <th className='bx-stations-table__th bx-stations-table__th--status'>Status</th>
                  <th className='bx-stations-table__th bx-stations-table__th--status'>Date</th>
                  <th className='bx-stations-table__th bx-stations-table__th--status'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td className='bx-stations-table__td' colSpan={9}>
                      <HorizontalLoader></HorizontalLoader>
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td className='bx-stations-table__td' colSpan={9}>
                      No order found.
                    </td>
                  </tr>
                ) : (
                  orders.map((s: OrderXModel, index: Key) => (
                    <tr key={s.referenceNumber}>
                      <td className='bx-stations-table__td bx-stations-table__td--name'>
                        {Number(index) + 1 + (pageNumber - 1) * pageSize}
                      </td>
                      <td className='bx-stations-table__td bx-stations-table__td--name'>
                        {s.referenceNumber}
                      </td>
                      <td className='bx-stations-table__td bx-stations-table__td--vol'>
                        {s.user.firstName} {s.user.lastName}
                      </td>
                      <td className='bx-stations-table__td bx-stations-table__td--city'>
                        {s.product}
                      </td>

                      <td className='bx-stations-table__td bx-stations-table__td--vol'>
                        {s.quantity.toFixed(2)} Litres
                      </td>
                      <td className='bx-stations-table__td bx-stations-table__td--seen'>
                        {s.currency}
                        {CurrencyFormatter(s.costMajor)}
                      </td>
                      <td className='bx-stations-table__td bx-stations-table__td--seen'>
                        {s.paymentMethod}
                      </td>
                      <td className='bx-stations-table__td bx-stations-table__td--status'>
                        <div className='bx-stations-table__badge-wrap'>
                          <span
                            className={
                              s.status === 'IN_PROGRESS'
                                ? 'bx-stations-table__badge-pending'
                                : s.status === 'CANCELLED'
                                  ? 'bx-stations-table__badge-cancel'
                                  : 'bx-stations-table__badge'
                            }
                          >
                            {s.status}
                          </span>
                        </div>
                      </td>

                      <td className='bx-stations-table__td bx-stations-table__td--seen'>
                        {DateTimeFormatter(s.createdAt)}
                      </td>
                      <td className='bx-stations-table__td bx-stations-table__td--actions'>
                        <div className='bx-stations-table__menu' data-station-menu-root='true'>
                          <button
                            type='button'
                            className='bx-stations-table__menu-btn'
                            aria-haspopup='menu'
                            aria-expanded={openMenuId === s.resourceId}
                            onClick={() =>
                              setOpenMenuId((v) => (v === s.resourceId ? null : s.resourceId))
                            }
                          >
                            <svg
                              width='20'
                              height='20'
                              viewBox='0 0 20 20'
                              fill='none'
                              xmlns='http://www.w3.org/2000/svg'
                            >
                              <circle cx='10' cy='4.5' r='1.3' fill='#667085' />
                              <circle cx='10' cy='10' r='1.3' fill='#667085' />
                              <circle cx='10' cy='15.5' r='1.3' fill='#667085' />
                            </svg>
                          </button>

                          {openMenuId === s.resourceId ? (
                            <div className='bx-stations-table__menu-dropdown' role='menu'>
                              <button
                                type='button'
                                className='bx-stations-table__menu-item'
                                role='menuitem'
                                onClick={() => {
                                  navigate(`/boostxpress/orders/${s.resourceId}`)
                                }}
                              >
                                View
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <div className='bx-stations-pagination'>
              <button
                className='bx-stations-pagination__btn'
                disabled={pageNumber <= 1 || isLoading}
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              >
                <div className='bx-stations-pagination__btn-icon-wrap'>
                  <img
                    src={arrowLeft}
                    alt=''
                    className='bx-stations-pagination__btn-icon bx-stations-pagination__btn-icon--left'
                  />
                </div>
                <span className='bx-stations-pagination__btn-text'>Previous</span>
              </button>

              <div className='bx-stations-pagination__numbers'>
                <span className='bx-stations-pagination__number bx-stations-pagination__number--active'>
                  {pageNumber}
                </span>
                <span className='bx-stations-pagination__ellipsis'>/</span>
                <span className='bx-stations-pagination__number'>{totalPages}</span>
              </div>

              <button
                className='bx-stations-pagination__btn'
                disabled={pageNumber >= totalPages || isLoading}
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
              >
                <span className='bx-stations-pagination__btn-text'>Next</span>
                <img src={arrowRight} alt='' className='bx-stations-pagination__btn-icon' />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OrderBoost
